// Catalog wire ref grammar — SSOT for the lenient `[kind:]publisher/name#version`
// form (`@` scope sigil, `#` version). Rationale + consumers: core/CLAUDE.md;
// grammar spec: 2026-06-02-scoped-asset-ref-grammar.

export type { AssetRef } from "./models.js";
export {
  ASSET_NAME_HINT,
  assetRefToStr,
  isValidAssetName,
  parseAssetRef,
} from "./models.js";

/** Parsed catalog-wire ref. `publisher` never carries a leading `@`. */
export interface CatalogRef {
  /** Asset kind when the ref carried a `kind:` prefix; null otherwise. */
  kind: string | null;
  /** Publisher namespace, WITHOUT a leading `@`. */
  publisher: string;
  /** Asset name. */
  name: string;
  /** Version / pin when present; null otherwise. */
  version: string | null;
}

// [kind:]@?publisher/name[#version]; `name` is `[^#@]+` so a legacy `name@version`
// fails to match (returns null) rather than swallowing `@version` into the name.
const CATALOG_REF_RE = /^(?:([a-z][a-z0-9-]*):)?@?([^/]+)\/([^#@]+)(?:#(.+))?$/;

/**
 * Lenient parse to parts; total (returns null on malformed input, including a
 * legacy `@version` ref — `@` is scope-only). Mirrors the store's parseStoreRef.
 */
export function parseCatalogRef(ref: string): CatalogRef | null {
  const m = CATALOG_REF_RE.exec(ref);
  if (!m) {
    return null;
  }
  return { kind: m[1] ?? null, publisher: m[2], name: m[3], version: m[4] ?? null };
}

/**
 * Normalize a loose ref to the wire string: strip a leading `@`, rewrite a
 * post-name `@version` to `#version` (idempotent; version-less / slash-less pass
 * through). Mirrors the platform's toStoreRef.
 */
export function toCatalogWireRef(ref: string): string {
  const noScope = ref.startsWith("@") ? ref.slice(1) : ref;
  const slash = noScope.indexOf("/");
  if (slash < 0) {
    return noScope;
  }
  const tail = noScope.slice(slash + 1);
  const at = tail.indexOf("@");
  if (at < 0) {
    return noScope;
  }
  return `${noScope.slice(0, slash + 1)}${tail.slice(0, at)}#${tail.slice(at + 1)}`;
}
